/**
 * SystemNode — GridState pass-through system node.
 *
 * Receives GridState on `in`, applies a grid system function, pushes result to `out`.
 * Layer names are string params. Number params are tweakable.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { params as defineParams, internals, EndpointsRecord } from '@/nodes/TypedEndpoints';
import type { Dependencies } from '@nodes/Dependencies';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@/graph/Payload';
import type { GridState } from '../GridState';
import { makeRng } from '../GridState';
import { allTransformConfigs, type GridTransformConfig } from './gridTransformConfigs';

// ─── SystemNode class ────────────────────────────────────────

class GridTransformNode<T extends EndpointsRecord = EndpointsRecord> extends BaseNode<T> {
  protected config: GridTransformConfig;
  private rng = makeRng(42);

  constructor(id: string, config: GridTransformConfig, deps: Dependencies) {
    super(id, deps);
    this.config = config;
  }

  onInternalChanged(payload: Payload, endpointId: string): void {
    if (endpointId !== 'in') return;

    const state = (payload as any)?.value ?? payload;
    if (!state || typeof state !== 'object' || !('layers' in state)) return;

    const gridState = state as GridState;
    const paramValues = this.collectParams();

    // Check required layer params exist
    for (const [key, spec] of Object.entries(this.config.layerParams)) {
      if (!paramValues[key] && !spec.info?.includes('optional')) {
        // Skip execution if required layer not specified
        this.deps.endpointApi.setValue('out',gridState);
        return;
      }
    }

    const system = this.config.createGridTransform(paramValues);
    const result = system(gridState, this.rng);
    this.deps.endpointApi.setValue('out',result);
  }

  private collectParams(): Record<string, any> {
    const result: Record<string, any> = {};
    // Layer name params (string type)
    for (const key of Object.keys(this.config.layerParams)) {
      const val = this.deps.endpointApi.getValue(key);
      result[key] = typeof val === 'string' ? val : (val?.value ?? '');
    }
    // Number/option params
    for (const key of Object.keys(this.config.params)) {
      result[key] = (this as any).params?.[key]?.value ?? this.config.params[key].value;
    }
    return result;
  }
}

// ─── Generate NodeDefs from configs ──────────────────────────

export const gridTransformNodes: NodeDef[] = allTransformConfigs.map(config => {
  // Build endpoints: in/out internals + layer string params + number params
  const layerStringParams: Record<string, any> = {};
  for (const [key, spec] of Object.entries(config.layerParams)) {
    layerStringParams[key] = {
      dataType: 'string',
      value: '',
      label: spec.label,
      info: spec.info,
    };
  }

  const numberParams: Record<string, any> = {};
  for (const [key, spec] of Object.entries(config.params)) {
    numberParams[key] = spec;
  }

  const endpoints = {
    ...defineParams({ ...layerStringParams, ...numberParams }),
    ...internals({
      in:  { dataType: 'any' as any, isInput: true, label: 'In' },
      out: { dataType: 'any' as any, isOutput: true, label: 'Out' },
    }),
  };

  return {
    type: config.type,
    shortName: config.shortName,
    label: config.label,
    info: config.info,
    category: 'transform',
    domain: Domain.Data,
    endpoints,
    ctor: class extends GridTransformNode<typeof endpoints> {
      constructor(id: string, deps: Dependencies) {
        super(id, config, deps);
      }
    },
  };
});
